import React, { useState, useEffect } from 'react';

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_ATTEMPTS = 10; // 20 seconds total

interface DialogProps {
  title: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
}

const Dialog: React.FC<DialogProps> = ({ title, description, buttonText, onButtonClick }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80">
    <div className="bg-slate-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
      <h2 className="text-xl font-semibold text-slate-100 mb-2">
        {title}
      </h2>
      <p className="text-slate-300 mb-6">
        {description}
      </p>
      <div className="flex justify-end">
        <button
          onClick={onButtonClick}
          className="px-4 py-2 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400"
        >
          {buttonText}
        </button>
      </div>
    </div>
  </div>
);

interface PaymentSuccessHandlerProps {
  onSuccess: () => Promise<void>;
}

const PaymentSuccessHandler: React.FC<PaymentSuccessHandlerProps> = ({ onSuccess }) => {
  const [status, setStatus] = useState<'checking' | 'success' | 'error' | 'auth_error' | 'timeout'>('checking');
  const [hasProcessedSuccess, setHasProcessedSuccess] = useState(false);

  useEffect(() => {
    let attempts = 0;
    let timeoutId: number | undefined;
    let isActive = true; // Flag to prevent state updates after unmount

    const checkStatus = async () => {
      if (!isActive) return false;
      
      try {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
          throw new Error('No session token found');
        }

        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setStatus('auth_error');
            return true;
          }
          throw new Error('api_error');
        }

        const data = await response.json();
        
        if (data.tier !== 'free') {
          if (!hasProcessedSuccess && isActive) {
            setStatus('success');
            setHasProcessedSuccess(true);
            await onSuccess();
            window.history.replaceState({}, '', '/');
          }
          return true;
        }

        return false;
      } catch (error) {
        if (error instanceof Error && error.message === 'auth_error') {
          setStatus('auth_error');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      if (!isActive) return;
      
      const success = await checkStatus();
      
      if (success || !isActive) {
        return; // Stop polling on success or if component is unmounted
      }

      attempts++;
      if (attempts >= MAX_ATTEMPTS) {
        setStatus('timeout');
      } else if (isActive) {
        timeoutId = window.setTimeout(poll, POLL_INTERVAL);
      }
    };

    // Start polling
    poll();

    // Cleanup function
    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onSuccess, hasProcessedSuccess]);

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80">
        <div className="bg-slate-800 p-6 rounded-lg shadow-xl text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">
            Activating Your Subscription
          </h2>
          <p className="text-slate-300">
            Please wait while we activate your subscription...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'auth_error') {
    return (
      <Dialog
        title="Session Expired"
        description="Your session has expired. Please log in again to view your subscription status."
        buttonText="Log In Again"
        onButtonClick={() => window.location.reload()}
      />
    );
  }

  if (status === 'timeout' || status === 'error') {
    return (
      <Dialog
        title="Subscription Status Pending"
        description="Your subscription is being processed. This may take a few minutes. You can continue using the app, and your new features will be available shortly."
        buttonText="Refresh Now"
        onButtonClick={() => window.location.reload()}
      />
    );
  }

  // Don't show anything if success (will be redirected) or other states
  return null;
};

export default PaymentSuccessHandler;
